<template>
  <v-container fluid class="customer-finance">
    <v-row v-if="loading" class="mt-10" justify="center">
      <v-progress-circular indeterminate color="primary-500" size="46" />
    </v-row>

    <template v-else>
      <v-row dense>
        <v-col cols="12">
          <v-card class="customer-finance__hero" elevation="0">
            <v-card-text class="customer-finance__hero-content">
              <div>
                <div class="customer-finance__eyebrow">Minha carteira</div>
                <div class="text-h4 font-weight-bold">
                  {{ finance.referenceLabel || "Carteira" }}
                </div>
                <div class="customer-finance__subtitle mt-3">
                  Consulte seus Pitch Coins e as cobranças do mês.
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense class="mt-6">
        <v-col cols="12" md="4">
          <v-card class="customer-finance__summary" elevation="0">
            <v-card-text>
              <div class="customer-finance__summary-label">
                Próxima cobrança
              </div>
              <div class="customer-finance__summary-value">
                {{
                  nextPendingCharge
                    ? formatCurrency(nextPendingCharge.amount)
                    : "Sem pendências"
                }}
              </div>
              <div
                v-if="nextPendingCharge"
                class="customer-finance__summary-helper"
              >
                Vencimento em {{ nextPendingCharge.dueDateLabel }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="customer-finance__summary" elevation="0">
            <v-card-text>
              <div class="customer-finance__summary-label">Pitch Coins</div>
              <div class="customer-finance__summary-value">
                {{ formatCurrency(finance.wallet.coinBalance) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="customer-finance__summary" elevation="0">
            <v-card-text>
              <div class="customer-finance__summary-label">Total pendente</div>
              <div class="customer-finance__summary-value">
                {{ formatCurrency(finance.summary.totalPendingAmount || 0) }}
              </div>
              <div class="customer-finance__summary-helper">
                {{ finance.summary.pendingCount || 0 }} cobrança{{
                  finance.summary.pendingCount === 1 ? "" : "s"
                }}
                pendente{{ finance.summary.pendingCount === 1 ? "" : "s" }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense class="mt-4">
        <v-col cols="12">
          <v-card class="customer-finance__surface" elevation="0">
            <v-card-text>
              <div class="customer-finance__section-title">Mensalidades</div>

              <div
                v-if="finance.items.length"
                class="customer-finance__charge-list mt-5"
              >
                <div
                  v-for="charge in finance.items"
                  :key="charge.id"
                  class="customer-finance__charge-item"
                >
                  <div>
                    <div class="customer-finance__charge-title">
                      {{ charge.classCode }}
                    </div>
                    <div class="customer-finance__charge-meta">
                      {{ charge.partnerName }} · {{ charge.referenceLabel }} ·
                      venc. {{ charge.dueDateLabel }}
                    </div>
                  </div>

                  <div class="d-flex align-center">
                    <strong class="customer-finance__charge-amount mr-3">
                      {{ formatCurrency(charge.amount) }}
                    </strong>
                    <v-chip small dark :color="statusColor(charge.status)">
                      {{ statusLabel(charge.status) }}
                    </v-chip>
                  </div>
                </div>
              </div>

              <v-alert v-else type="info" text class="mb-0 mt-5">
                Nenhuma cobrança registrada para este período.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import customerApi from "@/shared/pitchCustomerApi";

export default {
  name: "CustomerFinance",

  data() {
    return {
      loading: true,
      finance: {
        wallet: {
          cashBalance: 0,
          coinBalance: 0,
        },
        summary: {
          pendingCount: 0,
        },
        items: [],
        referenceLabel: "",
      },
    };
  },

  async created() {
    await this.loadFinance();
  },

  computed: {
    nextPendingCharge() {
      return this.finance.items.find((item) => item.status !== "PAID") || null;
    },
  },

  methods: {
    async loadFinance() {
      this.loading = true;

      try {
        const res = await customerApi.getFinance();
        this.finance = {
          ...this.finance,
          ...res.data,
        };
      } catch (_error) {
        this.$root.$emit("display-alert", {
          text: "Nao foi possivel carregar a carteira do aluno.",
          type: 1,
        });
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(value || 0));
    },
    statusLabel(status) {
      if (status === "PAID") {
        return "Paga";
      }

      if (status === "OVERDUE") {
        return "Atrasada";
      }

      return "Pendente";
    },
    statusColor(status) {
      if (status === "PAID") {
        return "success";
      }

      if (status === "OVERDUE") {
        return "error";
      }

      return "warning";
    },
  },
};
</script>

<style scoped>
.customer-finance {
  padding-bottom: 32px;
}

.customer-finance__hero,
.customer-finance__summary,
.customer-finance__surface {
  border-radius: 24px;
  background: #ffffff;
  border: 1px solid rgba(232, 85, 38, 0.12);
  box-shadow: 0 24px 48px -38px rgba(190, 24, 93, 0.25);
}

.customer-finance__hero {
  background: linear-gradient(
    135deg,
    rgba(232, 85, 38, 0.18),
    rgba(255, 255, 255, 0.72)
  );
}

.customer-finance__hero-content {
  padding: 28px;
}

.customer-finance__eyebrow {
  color: #e85526;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.customer-finance__subtitle {
  color: #6b7280;
  max-width: 700px;
  line-height: 1.6;
}

.customer-finance__summary-label {
  color: #6b7280;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.customer-finance__summary-value {
  margin-top: 10px;
  color: #1f2937;
  font-size: 1.55rem;
  font-weight: 800;
}

.customer-finance__summary-helper {
  margin-top: 8px;
  color: #6b7280;
  line-height: 1.5;
}

.customer-finance__section-title {
  color: #1f2937;
  font-size: 1.05rem;
  font-weight: 700;
}

.customer-finance__charge-list {
  display: grid;
  gap: 12px;
}

.customer-finance__charge-item {
  padding: 16px 18px;
  border-radius: 18px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.customer-finance__charge-title {
  color: #1f2937;
  font-weight: 700;
}

.customer-finance__charge-meta {
  margin-top: 4px;
  color: #6b7280;
}

.customer-finance__charge-amount {
  color: #e85526;
}

@media (max-width: 700px) {
  .customer-finance__charge-item {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
